import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { categories, siteInfo } from "@/lib/movieData";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/search", label: "搜索" },
  { to: "/about", label: "关于" },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const categoryActive = location.pathname.startsWith("/category");

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled || mobileOpen
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200"
          : "bg-background-50 border-b border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="h-[68px] md:h-[76px] flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 cursor-pointer shrink-0">
            <span className="w-10 h-10 md:w-11 md:h-11 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center font-heading text-xl md:text-2xl">
              秋
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg md:text-xl font-semibold tracking-wide text-foreground-950">
                秋霞电影
              </span>
              <span className="hidden sm:block mt-1 text-[10px] tracking-[0.28em] text-foreground-500 uppercase">
                {siteInfo.en}
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.slice(0, 1).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                    isActive
                      ? "text-primary-600 font-medium bg-primary-50"
                      : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}

            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setCategoryOpen((prev) => !prev)}
                className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
                  categoryActive
                    ? "text-primary-600 font-medium bg-primary-50"
                    : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                }`}
              >
                电影分类
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    categoryOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>
              {categoryOpen && (
                <div className="absolute left-0 top-full mt-3 w-[420px] p-3 rounded-lg bg-background-50 border border-background-200 animate-fade-in">
                  <div className="grid grid-cols-2 gap-1">
                    {categories.map((category) => (
                      <Link
                        key={category.slug}
                        to={`/category/${category.slug}`}
                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <span className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                          <i className={`${category.icon} text-sm`}></i>
                        </span>
                        <span className="text-sm text-foreground-800">{category.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {navItems.slice(1).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                    isActive
                      ? "text-primary-600 font-medium bg-primary-50"
                      : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/search"
              aria-label="搜索电影"
              className="w-10 h-10 flex items-center justify-center rounded-md border border-background-200 text-foreground-700 hover:text-accent-600 hover:border-accent-300 transition-colors cursor-pointer"
            >
              <i className="ri-search-line text-lg"></i>
            </Link>
            <Link
              to="/category/action"
              className="hidden lg:inline-flex items-center gap-2 h-10 px-5 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-film-line"></i>
              浏览片库
            </Link>
            <button
              type="button"
              aria-label="展开菜单"
              onClick={() => setMobileOpen((prev) => !prev)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-md border border-background-200 text-foreground-800 cursor-pointer"
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 max-h-[calc(100vh-68px)] overflow-y-auto">
          <div className="px-4 py-4">
            <div className="flex flex-col gap-1">
              <Link to="/" className="px-3 py-3 rounded-md text-sm text-foreground-800 hover:bg-background-100">
                首页
              </Link>
              <Link
                to="/search"
                className="px-3 py-3 rounded-md text-sm text-foreground-800 hover:bg-background-100"
              >
                搜索电影
              </Link>
              <Link
                to="/about"
                className="px-3 py-3 rounded-md text-sm text-foreground-800 hover:bg-background-100"
              >
                关于我们
              </Link>
            </div>
            <p className="mt-4 mb-2 px-3 text-xs tracking-widest text-foreground-500">电影分类</p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((category) => (
                <Link
                  key={category.slug}
                  to={`/category/${category.slug}`}
                  className="flex items-center gap-2 px-3 py-3 rounded-md bg-background-100 text-sm text-foreground-800"
                >
                  <span className="w-6 h-6 flex items-center justify-center text-primary-600">
                    <i className={`${category.icon} text-base`}></i>
                  </span>
                  {category.name}
                </Link>
              ))}
            </div>
            <Link
              to="/category/action"
              className="mt-4 flex items-center justify-center gap-2 h-11 rounded-md bg-accent-500 text-background-50 text-sm font-medium"
            >
              <i className="ri-film-line"></i>
              进入片库
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}